import { useState } from "react";
import { toast } from "sonner";
import { createDisposition } from "@/lib/api";
import { DISPOSITIONS, TREATMENTS } from "@/lib/dispositionConstants";

/** Consultant-authored disposition for an object the rules did not flag. */
export default function ManualDispositionForm({ workspaceId, onCreated, onCancel }) {
  const [form, setForm] = useState({ object_kind: "physical-file", library_name: "", object_name: "", technical_treatment: "", migration_disposition: "", rationale: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const created = await createDisposition(workspaceId, {
        ...form,
        technical_treatment: form.technical_treatment || null,
        migration_disposition: form.migration_disposition || null,
        rationale: form.rationale || null,
      });
      toast.success(`Added ${created.library_name}/${created.object_name} as a proposed disposition.`);
      onCreated?.(created);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add disposition.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="ml-card p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end" data-testid="manual-disposition-form">
      <label className="text-xs space-y-1">
        <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Kind</span>
        <select className="ml-input" value={form.object_kind} onChange={set("object_kind")} data-testid="manual-kind">
          <option value="physical-file">physical-file</option>
          <option value="program">program</option>
        </select>
      </label>
      <label className="text-xs space-y-1">
        <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Library</span>
        <input className="ml-input ml-mono uppercase" required maxLength={64} value={form.library_name} onChange={set("library_name")} data-testid="manual-library" />
      </label>
      <label className="text-xs space-y-1">
        <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Object</span>
        <input className="ml-input ml-mono uppercase" required maxLength={64} value={form.object_name} onChange={set("object_name")} data-testid="manual-object" />
      </label>
      <label className="text-xs space-y-1">
        <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Treatment</span>
        <select className="ml-input" value={form.technical_treatment} onChange={set("technical_treatment")} data-testid="manual-treatment">
          <option value="">—</option>
          {TREATMENTS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </label>
      <label className="text-xs space-y-1">
        <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Disposition</span>
        <select className="ml-input" value={form.migration_disposition} onChange={set("migration_disposition")} data-testid="manual-disposition">
          <option value="">—</option>
          {DISPOSITIONS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </label>
      <div className="flex gap-2">
        <button type="submit" className="ml-btn-primary" disabled={busy} data-testid="manual-submit">{busy ? "Adding…" : "Add"}</button>
        <button type="button" className="ml-btn-ghost" onClick={onCancel} data-testid="manual-cancel">Cancel</button>
      </div>
      <label className="text-xs space-y-1 col-span-2 md:col-span-6">
        <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Rationale (optional)</span>
        <input className="ml-input" value={form.rationale} onChange={set("rationale")} data-testid="manual-rationale" placeholder="Why this object needs a position the rules did not propose" />
      </label>
    </form>
  );
}
